/**
 * Kennisbank content model.
 *
 * Eén artikel = één object in `articles`. Voeg een nieuw object toe en het
 * artikel krijgt automatisch een route (/kennisbank/<slug>), een eigen
 * title/description, een plek in het overzicht, de sitemap en de prerender.
 */

export type ArticleSection = { heading: string; body: string[] };

export type Article = {
  slug: string;
  /** <h1> op de artikelpagina */
  h1: string;
  /** <title> (max ~60 tekens) */
  title: string;
  /** meta description (max ~155 tekens) */
  description: string;
  /** Korte samenvatting in het overzicht en als intro */
  excerpt: string;
  /** ISO datum, gebruikt voor Article-schema en sitemap lastmod */
  datePublished: string;
  dateModified?: string;
  readingTime: string;
  sections: ArticleSection[];
  /** Interne links terug naar dienst- of stadpagina's */
  related: { label: string; path: string }[];
};

export const articles: Article[] = [
  {
    slug: "wat-kost-een-vr-bedrijfsuitje",
    h1: "Wat kost een VR-bedrijfsuitje? Prijzen en voorbeelden",
    title: "Wat kost een VR-bedrijfsuitje? | Prijzen – PlayfieldVR",
    description:
      "Wat kost een VR-bedrijfsuitje? Prijsopbouw, rekenvoorbeelden voor 10, 20 en 40 personen en wat er inbegrepen zit. Vraag een voorstel aan.",
    excerpt:
      "Een realistisch beeld van de prijsopbouw van een VR-bedrijfsuitje, met rekenvoorbeelden voor teams van 10, 20 en 40 personen.",
    datePublished: "2026-08-24",
    readingTime: "5 min",
    sections: [
      {
        heading: "Waar de prijs van afhangt",
        body: [
          "De prijs van een VR-bedrijfsuitje op locatie wordt bepaald door drie dingen: het aantal deelnemers, de duur van het programma en de reisafstand naar jullie locatie. Omdat wij de complete opstelling meenemen, zit opbouw, begeleiding en afbouw altijd in de prijs.",
          "Bij PlayfieldVR begint een programma vanaf € 20,- per persoon. Dat tarief geldt bij een volle bezetting; kleinere groepen betalen relatief iets meer omdat de crew en set-up dezelfde tijd kosten.",
        ],
      },
      {
        heading: "Rekenvoorbeelden",
        body: [
          "Team van 10 personen: een programma van circa 1,5 uur, waarin elk groepje twee sessies speelt. Compact, ideaal als afsluiting van een werkdag.",
          "Team van 20 personen: reken op 2 tot 3 uur inclusief wissels. Dit is de meest gekozen variant voor een afdelingsuitje.",
          "Team van 40 personen: een dagdeel van circa 4 uur, vaak gecombineerd met een lunch of borrel zodat er altijd een deel van de groep speelt.",
        ],
      },
      {
        heading: "Wat zit er inbegrepen",
        body: [
          "Professionele VR-headsets en tracking, een selectie uit onze acht curated games, begeleiding door onze crew, volledige opbouw en afbouw en desinfectie van de apparatuur tussen sessies.",
          "Wat er niet in zit: catering en zaalhuur. Heb je zelf geen geschikte ruimte, dan denken we mee over een locatie in de buurt.",
        ],
      },
      {
        heading: "Hoe kom je aan een exacte prijs",
        body: [
          "Laat weten wanneer, waar en met hoeveel mensen je wilt spelen. Je ontvangt doorgaans binnen één werkdag een voorstel op maat, zonder verplichtingen.",
        ],
      },
    ],
    related: [
      { label: "VR bedrijfsuitje op locatie", path: "/vr-bedrijfsuitje" },
      { label: "VR personeelsuitje", path: "/vr-personeelsuitje" },
    ],
  },
  {
    slug: "vr-teambuilding-vs-escape-room",
    h1: "VR teambuilding vs. escape room: wat past bij jouw team?",
    title: "VR teambuilding vs. escape room | PlayfieldVR",
    description:
      "VR teambuilding of een klassieke escape room? Vergelijk groepsgrootte, reistijd, toegankelijkheid en teameffect. Vraag een voorstel aan.",
    excerpt:
      "Een eerlijke vergelijking tussen VR teambuilding op locatie en een klassieke escape room, op groepsgrootte, reistijd en teameffect.",
    datePublished: "2026-08-24",
    readingTime: "4 min",
    sections: [
      {
        heading: "Groepsgrootte",
        body: [
          "Een fysieke escape room werkt meestal tot 6 personen per kamer. Met een team van 20 betekent dat meerdere boekingen, wachttijd of een andere locatie. Een mobiele VR-opstelling schaalt eenvoudiger: wij spelen in sessies door en houden de hele groep in beweging.",
        ],
      },
      {
        heading: "Reistijd en organisatie",
        body: [
          "Bij een escape room reist het team naar de locatie. Bij VR teambuilding op locatie reizen wij naar jullie kantoor of zaal. Dat scheelt uren en maakt het mogelijk om een activiteit in een gewone werkdag te plannen.",
        ],
      },
      {
        heading: "Toegankelijkheid",
        body: [
          "Escape rooms zijn intuïtief, maar VR heeft de reputatie technisch te zijn. In de praktijk is de uitleg in drie minuten klaar en spelen collega's die nooit gamen even makkelijk mee. Wie liever niet in de headset gaat, heeft een rol als coach vanaf de zijlijn.",
        ],
      },
      {
        heading: "Wat blijft er hangen",
        body: [
          "Beide vormen dwingen tot overleg en rolverdeling. Het verschil zit in de nieuwigheid: bijna iedereen heeft een escape room gedaan, free-roam VR nog niet. Dat zorgt voor een sterker gespreksonderwerp in de weken erna.",
          "Kort samengevat: kies een escape room voor een klein team dat er even uit wil, en VR teambuilding voor grotere groepen, korte doorlooptijd en een activiteit die op jullie eigen locatie past.",
        ],
      },
    ],
    related: [
      { label: "VR teamuitje op locatie", path: "/vr-teamuitje" },
      { label: "VR teambuilding Amsterdam", path: "/vr-teambuilding-amsterdam" },
    ],
  },
  {
    slug: "hoeveel-ruimte-vr-op-kantoor",
    h1: "Hoeveel ruimte heb je nodig voor VR op kantoor?",
    title: "Hoeveel ruimte nodig voor VR op kantoor? – PlayfieldVR",
    description:
      "De ideale ruimte voor free-roam VR op kantoor: afmetingen, plafondhoogte, vloer en stroom. Check of jouw locatie geschikt is.",
    excerpt:
      "Concrete richtlijnen voor afmetingen, plafondhoogte, vloer en stroom, zodat je vooraf weet of jouw kantoor geschikt is voor VR.",
    datePublished: "2026-08-24",
    readingTime: "3 min",
    sections: [
      {
        heading: "De ideale afmeting",
        body: [
          "Voor een free-roam opstelling werken we het liefst in een ruimte van circa 8 bij 8 meter met minimaal 2,5 meter vrije hoogte. Daarin kan een groepje van maximaal zes spelers echt rondlopen zonder elkaar in de weg te zitten.",
        ],
      },
      {
        heading: "Kleinere ruimtes",
        body: [
          "Heb je minder ruimte? Dat hoeft geen probleem te zijn. Vanaf ongeveer 5 bij 5 meter stemmen we de gameselectie af op de beschikbare vloer: titels die op één plek gespeeld worden in plaats van vrij rondlopen.",
        ],
      },
      {
        heading: "Vloer, licht en stroom",
        body: [
          "Een vlakke vloer zonder losse kabels of drempels, geen direct fel zonlicht op de trackingzone en één standaard stopcontact zijn genoeg. Wij boren niets in vloeren of muren en laten de ruimte achter zoals we hem aantroffen.",
        ],
      },
      {
        heading: "Twijfel je over jullie locatie?",
        body: [
          "Stuur ons de afmetingen of een foto van de ruimte. Wij doen vooraf een korte check en geven eerlijk aan wat er wel en niet mogelijk is.",
        ],
      },
    ],
    related: [
      { label: "VR borrelactiviteit op kantoor", path: "/vr-borrelactiviteit-kantoor" },
      { label: "VR bedrijfsuitje op locatie", path: "/vr-bedrijfsuitje" },
    ],
  },
];

export const getArticle = (slug?: string) =>
  articles.find((article) => article.slug === slug);
